// @workflow assistant.attachments
/**
 * Attachments in the composer as pure state (contract "Attachments (draft v1)"): the server's
 * limits, the codes, the browser's own checks against those limits, and the chips above the box,
 * each idle → uploading → ready | error. Every function returns a new list, or the same list when
 * nothing changes. The words are in attachmentMessages.js.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { codeForRefusal as codeFor } from './refusalCodes.js';

export const ATTACHMENT_CODE = Object.freeze({
  TOO_LARGE: 'attachment.too_large',
  UNSUPPORTED_TYPE: 'attachment.unsupported_type',
  EMPTY: 'attachment.empty',
  UNREADABLE: 'attachment.unreadable',
  UNAVAILABLE: 'attachment.unavailable',
  // The message POST's, when its attachment ids can't be used.
  NOT_FOUND: 'attachment.not_found',
  TOO_MANY: 'attachment.too_many',
  // The browser's, never sent by the server: the upload did not get an answer.
  FAILED: 'attachment.failed',
});

/** The codes the server may send. */
export const SERVER_CODES = Object.freeze([
  ATTACHMENT_CODE.TOO_LARGE,
  ATTACHMENT_CODE.UNSUPPORTED_TYPE,
  ATTACHMENT_CODE.EMPTY,
  ATTACHMENT_CODE.UNREADABLE,
  ATTACHMENT_CODE.UNAVAILABLE,
]);

/** For a refusal without a readable code (a 413 from the web server in front of the API has no JSON). */
const CODE_BY_STATUS = Object.freeze({
  413: ATTACHMENT_CODE.TOO_LARGE,
  415: ATTACHMENT_CODE.UNSUPPORTED_TYPE,
  422: ATTACHMENT_CODE.UNREADABLE,
  503: ATTACHMENT_CODE.UNAVAILABLE,
});

/** A message refused for its files: the draft and its chips go back to the composer. */
const MESSAGE_REFUSAL_CODES = Object.freeze([ATTACHMENT_CODE.NOT_FOUND, ATTACHMENT_CODE.TOO_MANY]);
export const isMessageRefusal = (code) => MESSAGE_REFUSAL_CODES.includes(code);

/** A refused request → its code: the body's when the contract lists it, else by status, else failed. */
export function codeForRefusal(status, body) {
  return codeFor(status, body, { codes: SERVER_CODES, byStatus: CODE_BY_STATUS, fallback: ATTACHMENT_CODE.FAILED });
}

export const CHIP_STATUS = Object.freeze({ UPLOADING: 'uploading', READY: 'ready', ERROR: 'error' });

const SERVER_STATUS = Object.freeze({ READY: 'ready', REJECTED: 'rejected' });
const EXTENSION = /(\.[a-z0-9]{1,8})$/i;
const positiveInt = (v) => Number.isInteger(v) && v > 0;
const words = (list) => (Array.isArray(list) ? list.filter((v) => typeof v === 'string' && v).map((v) => v.toLowerCase()) : []);

/**
 * GET …/attachments/limits → `{ maxBytes, maxPerMessage, kinds, accept, available }`, or null when
 * the body is not usable (then attaching is "unavailable"). Never hard-coded in the UI.
 * `available` is the server's file storage: false when it is down. An older server doesn't send
 * it, so only an explicit false turns it off.
 */
export function readLimits(body) {
  if (!body || !positiveInt(body.maxBytes) || !positiveInt(body.maxPerMessage)) return null;
  const accept = words(body.accept);
  if (!accept.length) return null;
  return { maxBytes: body.maxBytes, maxPerMessage: body.maxPerMessage, kinds: words(body.kinds), accept, available: body.available !== false };
}

/** The limits say the file storage is down: the paperclip waits, and a refused upload can be tried again. */
export const isStorageDown = (limits) => !!limits && limits.available === false;

/**
 * The limits are read again at most this often: the server checks its storage and keeps the answer
 * this long. The page keeps limits (down or not) for this long, and while the storage is down the
 * composer asks again after it.
 */
export const LIMITS_RECHECK_MS = 30 * 1000;

const extensionOf = (name) => {
  const match = String(name || '').match(EXTENSION);
  return match ? match[1].toLowerCase() : '';
};

/** The kind the chip shows until the server says (it reads the bytes): the extension without its dot. */
const guessKind = (name) => extensionOf(name).slice(1) || null;

/**
 * The browser's own check before uploading (no 14 MB upload just to be refused): empty, too
 * large, or a type the server does not take. The server checks everything again.
 * @returns {string|null} a code, or null when the file may go up
 */
export function checkFile({ name, size }, limits) {
  if (!size) return ATTACHMENT_CODE.EMPTY;
  if (size > limits.maxBytes) return ATTACHMENT_CODE.TOO_LARGE;
  if (!limits.accept.includes(extensionOf(name))) return ATTACHMENT_CODE.UNSUPPORTED_TYPE;
  return null;
}

/** Only uploading and ready chips count toward the per-message limit: an error chip is never sent. */
const counts = (chip) => chip.status === CHIP_STATUS.UPLOADING || chip.status === CHIP_STATUS.READY;

export const atLimit = (chips, limits) => !!limits && chips.filter(counts).length >= limits.maxPerMessage;

const newChip = ({ key, name, size }, code) => ({
  key,
  name,
  size,
  kind: guessKind(name),
  status: code ? CHIP_STATUS.ERROR : CHIP_STATUS.UPLOADING,
  progress: 0,
  id: null,
  reason: null,
  code: code || null,
  canRetry: false,
  rows: null,
  columns: null,
});

/**
 * Picked or dropped files → chips. A file past the limit is not added (`notAdded` names it).
 * @param {Array} chips
 * @param {Array<{ key: string, name: string, size: number }>} files
 * @returns {{ chips: Array, toUpload: string[], notAdded: string[] }}
 */
export function addFiles(chips, files, limits) {
  let next = chips;
  const toUpload = [];
  const notAdded = [];
  files.forEach((file) => {
    if (atLimit(next, limits)) {
      notAdded.push(file.name);
      return;
    }
    const code = checkFile(file, limits);
    next = [...next, newChip(file, code)];
    if (!code) toUpload.push(file.key);
  });
  return { chips: next, toUpload, notAdded };
}

function update(chips, key, fn) {
  const at = chips.findIndex((c) => c.key === key);
  if (at < 0) return chips;
  const changed = fn(chips[at]);
  if (changed === chips[at]) return chips;
  return chips.map((c, i) => (i === at ? changed : c));
}

const clamp01 = (v) => Math.min(1, Math.max(0, Number(v) || 0));

export function progressed(chips, key, fraction) {
  return update(chips, key, (c) => (c.status === CHIP_STATUS.UPLOADING ? { ...c, progress: clamp01(fraction) } : c));
}

/** The server's answer to the upload: ready (with its id and meta) or rejected (with its reason). */
export function uploaded(chips, key, body) {
  return update(chips, key, (c) => {
    if (c.status !== CHIP_STATUS.UPLOADING) return c;
    if (body && body.status === SERVER_STATUS.READY && body.id) {
      return {
        ...c,
        status: CHIP_STATUS.READY,
        progress: 1,
        id: String(body.id),
        name: body.name || c.name,
        kind: body.kind || c.kind,
        size: Number.isFinite(body.size) ? body.size : c.size,
        rows: Number.isFinite(body.rows) ? body.rows : null,
        columns: Number.isFinite(body.columns) ? body.columns : null,
      };
    }
    if (body && body.status === SERVER_STATUS.REJECTED) {
      return { ...c, status: CHIP_STATUS.ERROR, reason: typeof body.reason === 'string' && body.reason ? body.reason : null, code: body.reason ? null : ATTACHMENT_CODE.UNREADABLE };
    }
    return { ...c, status: CHIP_STATUS.ERROR, code: ATTACHMENT_CODE.FAILED, canRetry: true };
  });
}

/** The codes the same file can go up again after: no answer, or the storage was down. */
const RETRYABLE_CODES = Object.freeze([ATTACHMENT_CODE.FAILED, ATTACHMENT_CODE.UNAVAILABLE]);

/**
 * The server's own words are shown as given only for unavailable: that code covers both the storage
 * being down and the assistant being off, and only the server knows which. Other codes are worded here.
 */
const SERVER_WORDED_CODES = Object.freeze([ATTACHMENT_CODE.UNAVAILABLE]);
const serverWords = (code, message) => (SERVER_WORDED_CODES.includes(code) && typeof message === 'string' && message.trim() ? message.trim() : null);

/**
 * A refused or lost upload. Only a lost one, or one refused as unavailable, can be tried again.
 * @param {string|null} [serverMessage] the refusal's `message`
 */
export function failed(chips, key, code, serverMessage = null) {
  return update(chips, key, (c) => ({ ...c, status: CHIP_STATUS.ERROR, code, reason: serverWords(code, serverMessage), canRetry: RETRYABLE_CODES.includes(code) }));
}

export function retried(chips, key) {
  return update(chips, key, (c) => (c.canRetry ? { ...c, status: CHIP_STATUS.UPLOADING, progress: 0, code: null, reason: null, canRetry: false } : c));
}

export function removed(chips, key) {
  return chips.some((c) => c.key === key) ? chips.filter((c) => c.key !== key) : chips;
}

/** Send waits while any file is still going up. */
export const isUploading = (chips) => chips.some((c) => c.status === CHIP_STATUS.UPLOADING);

/** What goes with the message: the ready files only, as the stored user turn keeps them. */
export const readyAttachments = (chips) =>
  chips.filter((c) => c.status === CHIP_STATUS.READY).map(({ id, name, kind, size }) => ({ id, name, kind, size }));

/** A stored `user.message.attachments` → the chips of a sent turn; malformed entries are dropped. */
export function storedAttachments(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((a) => a && a.id && typeof a.name === 'string' && a.name)
    .map((a) => ({ id: String(a.id), name: a.name, kind: a.kind || null, size: Number.isFinite(a.size) ? a.size : null, available: a.available !== false }));
}

/** Sent attachments → ready chips again (a draft handed back after the server refused the message). */
export function readyChips(attachments, nextKey) {
  return (attachments || []).map((a) => ({ ...newChip({ key: nextKey(), name: a.name, size: a.size }, null), status: CHIP_STATUS.READY, progress: 1, id: a.id, kind: a.kind || null }));
}

/**
 * The server refused a message for its files: that user turn leaves the thread, and its text and
 * files come back as the draft (`null` when the turn is not there).
 * @returns {{ conversation: object, draft: { segments: Array, attachments: Array }|null }}
 */
export function returnDraft(conversation, messageId) {
  const turn = conversation.messages.find((m) => m.id === messageId);
  if (!turn) return { conversation, draft: null };
  return {
    conversation: { ...conversation, error: null, messages: conversation.messages.filter((m) => m.id !== messageId) },
    draft: { segments: turn.segments || [], attachments: turn.attachments || [] },
  };
}
